import type { JSX } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Outlet } from 'react-router-dom';

// Source image is 535x597 (not square) — `contain` on a rounded-square
// backdrop shows the whole seal; a hard circle clip with `cover` cropped
// the top/bottom ring text off.
const HOSPITAL_LOGO = (
  <Box
    sx={{
      width: 220,
      height: 220,
      borderRadius: '50%',
      bgcolor: '#fff',
      border: '6px solid rgba(255,255,255,0.9)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    }}
  >
    <Box
      component="img"
      src="/hospital-logo.jpg"
      alt="Hospital logo"
      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  </Box>
);

export function AuthLayout(): JSX.Element {
  return (
    <Box minHeight="100vh" display="flex" flexDirection={{ xs: 'column', md: 'row' }}>
      {/* Brand panel — 60% on desktop, hidden on mobile (the compact logo in the form panel covers it) */}
      <Box
        flexBasis={{ md: '60%' }}
        flexGrow={1}
        display={{ xs: 'none', md: 'flex' }}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={4}
        textAlign="center"
        sx={{
          background: 'linear-gradient(160deg, #C15F3C 0%, #8F3F26 100%)',
          color: '#fff',
          p: 6,
        }}
      >
        {HOSPITAL_LOGO}
        <Stack spacing={1} alignItems="center">
          <Typography variant="h3" fontWeight={700} sx={{ maxWidth: 520 }}>
            CSI Holdsworth Memorial Hospital
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.85, letterSpacing: 0.5 }}>
            Service with Compassion — since 1906
          </Typography>
        </Stack>
      </Box>

      {/* Login form — 40% on desktop, full width on mobile */}
      <Box
        flexBasis={{ md: '40%' }}
        flexGrow={{ xs: 1, md: 0 }}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ bgcolor: 'background.default' }}
        p={{ xs: 3, md: 5 }}
      >
        <Stack spacing={3} sx={{ width: '100%', maxWidth: 380 }}>
          {/* Compact logo shown only when the brand panel is hidden (mobile) */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              width: 88,
              height: 88,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'divider',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1,
              p: 0.75,
            }}
          >
            <Box
              component="img"
              src="/hospital-logo.jpg"
              alt="Hospital logo"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
          <Stack spacing={0.5} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Typography variant="h4" fontWeight={700} color="primary.main">
              Welcome to Staff Portal
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign={{ xs: 'center', md: 'left' }}>
              CSI Holdsworth Memorial Hospital — sign in to continue
            </Typography>
          </Stack>
          <Outlet />
        </Stack>
      </Box>
    </Box>
  );
}
